import React, { useEffect, useState } from 'react';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { Box } from '@mui/system';
import { StyledPageTitle } from 'theme/styles';
import { useTranslations } from 'hooks/useTranslations';
import Grid from '@mui/material/Grid';
import { FormSelect } from 'components/molecules/FormSelect';
import { FormDateTimePicker } from 'components/molecules/FormDateTimePicker';
import { Resolver, useFieldArray, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAncillaries } from 'pages/AncillariesPage/hooks';
import { Accordion } from 'components/molecules/Accordion';
import { StyledDetailsWrapper } from 'pages/Firearms/RegisteredFirearmDeleteCommentPage/styles';
import { Stack, Typography } from '@mui/material';
import { colors } from 'theme/colors';
import {
  createAncillaryMiddleTransaction,
  getPossibleAncillariesTransactionType,
} from 'requests/transaction';
import { EDictionaryTypes, IDictionary } from 'models/dictionary';
import { EDictionaryPrefixTypesUniqueId, mapDictionaryToOptionProp } from 'helpers/dictionary';
import { capitalizeFirstLetter } from 'utils/capitalize';
import { FormInput } from 'components/molecules/FormInput';
import {
  StyledIconWrapper,
  StyledLocationMarkConfirmationMarginBottom,
} from 'pages/LegalEntitiesPage/LegalEntityAddFirearmPage/styles';
import Icon from 'components/atoms/Icon/Icon';
import { EIconTypes } from 'constants/Icons';
import Button from 'components/atoms/Button';
import { ERouteLinks } from 'models/route';
import { EButtonSizes, EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { ETransactionTypes } from 'constants/TransactionTypes';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { useLang } from 'models/langContext';
import { FormDatePicker } from 'components/molecules/FormDatePicker';
import { EMIMETypes } from 'constants/MIMETypes';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { formatDateTimeToIso } from 'utils/date';
import { ActionTypes } from 'state/actions/alert';
import { useAlert } from 'models/alertContext';
import {
  EArtAncillaryTypeName,
  EArtAncillaryTypeNameLowerCase,
} from 'constants/ArtAccessoryType';
import Link from '@mui/material/Link';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { useScroll } from 'hooks/useScroll';
import { mapILegalEntityTypeToELegalEntityTypes } from 'models/legalEntity';
import { removeFileExtension } from 'utils/files';
import { StyledLegalLink } from 'components/atoms/StyledLegalLink';
import { FormWyswigEditor } from 'components/molecules/FormWyswigEditor';
import { countEditorCharacters } from 'components/organisms/HtmlSlate';
import { FormCheckbox } from 'components/molecules/FormCheckbox';
import { FormModal } from 'components/molecules/Modal';
import { typesWithoutStrictAddressLocation } from 'utils/transactionUtils';
import { getAttachmentError } from 'helpers/errors';
import { isPathEqualToRouteName } from 'utils/route';
import { EOrganisationCategory } from 'constants/OrganisationCategory';
import { FormSearchSelect } from 'components/molecules/FormSearchSelect';
import { SelectLocationTransactionPage } from 'pages/Transactions/SelectLocationTransactionPage';
import { MarkingsFormForMarked } from 'components/organisms/MarkingsFormForMarked';

const MAX_FILE_SIZE_IN_MB = 3;

let DISABLED_FOR_TRANSACTION_FORM: ETransactionTypes[] = [];

const CHANGE_LE_REQUIRED_TRANSACTION_TYPES = [
  ETransactionTypes.DECLARED_IMPORTED,
  ETransactionTypes.RECOVERED,
  ETransactionTypes.SEIZED,
  ETransactionTypes.SOLD,
  ETransactionTypes.GIVEN,
  ETransactionTypes.LEASED,
  ETransactionTypes.ASSIGNED_TEMPORARILY,
  ETransactionTypes.ASSIGNED_INDEFINITELY,
  ETransactionTypes.TRANSFERRED,
  ETransactionTypes.SURRENDERED,
  ETransactionTypes.RETURNED,
  ETransactionTypes.SURPLUS_SALE,
];

export enum MarkingTypes {
  IMPORT = 'IMPORT',
  EXPORT = 'EXPORT',
  DEACTIVATION = 'DEACTIVATION',
  CONFISCATION = 'CONFISCATION',
  SURPLUS_SALE = 'SURPLUS_SALE',
  APPLIED = 'APPLIED',
}

const getMarkingType = (typeUniqueId: string, markingType?: string) => {
  switch (typeUniqueId) {
    case ETransactionTypes.DECLARED_IMPORTED:
      return MarkingTypes.IMPORT;
    case ETransactionTypes.EXPORTED:
      return MarkingTypes.EXPORT;
    case ETransactionTypes.DEACTIVATED:
      return MarkingTypes.DEACTIVATION;
    case ETransactionTypes.MARKED:
      if (markingType === MarkingTypes.IMPORT) {
        return MarkingTypes.IMPORT;
      } else {
        return MarkingTypes.APPLIED;
      }
    case ETransactionTypes.SEIZED:
    case ETransactionTypes.RECOVERED:
    case ETransactionTypes.SURRENDERED:
    case ETransactionTypes.DECLARED_MANUFACTURED:
    case ETransactionTypes.DECLARED_UNKNOWN_ORIGIN:
      return MarkingTypes.APPLIED;
    case ETransactionTypes.CONFISCATED:
      return MarkingTypes.CONFISCATION;
    case ETransactionTypes.SURPLUS_SALE:
      return MarkingTypes.SURPLUS_SALE;
    default:
      break;
  }
};

export enum LETypes {
  individual = 'INDIVIDUAL',
  organization = 'ORGANIZATION',
}

const AncillariesAddTransactionPage = () => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { state: routerState, pathname }: any = useLocation();
  const { id = '' } = useParams<{ id: string }>();
  const { accessoryDetail, getAccessoryDetail, getAncillaryLastTransactionUniqueId } =
    useAncillaries();
  const [possibleTransactionDictionaries, setPossibleTransactionDictionaries] = useState<
    IDictionary[] | null
  >(null);
  const [isErrorLocation, setIsErrorLocation] = useState<boolean>(false);
  const [createTransactionError, setCreateTransactionError] = useState<string>('');
  const { isDesktop, isMobile } = useThemeBreakpoint();
  const { selectedLanguage } = useLang();
  const { setAlert } = useAlert();
  const { loadDictionaries, countryAll } = useLegalEntities();
  const [submitBlocked, setSubmitBlocked] = useState(false);
  const { elRef, scrollToError } = useScroll();
  const [lastTransactionUniqueId, setLastTransactionUniqueId] = useState<string | null>();
  const [newAncillaryLocationOwner, setNewAncillaryLocationOwner] = useState<string | null>(
    null,
  );
  const [newAncillaryLocationOwnerInserted, setNewAncillaryLocationOwnerInserted] =
    useState(false);
  const [unassignedLocationModalOpened, setUnassignedLocationModalOpened] = useState(false);
  const isMiddleTransactionPage = isPathEqualToRouteName(
    pathname,
    ERouteLinks.AncillariesMiddleTransaction,
  );

  const initialFormValues = {
    typeUniqueId: routerState?.typeUniqueId || '',
    transactionDate: routerState?.transactionDate || '',
    countryOfImportUniqueId: routerState?.countryOfImportUniqueId || '',
    importer: routerState?.importer || '',
    countryOfExportUniqueId: routerState?.countryOfExportUniqueId || '',
    expirationDate: routerState?.expirationDate || '',
    expirationTime: routerState?.expirationTime || '',
    attachments: routerState?.attachments?.length ? routerState?.attachments : [],
    legalEntityFullName: routerState?.legalEntityFullName || '',
    legalEntityId: routerState?.legalEntityId || '',
    description: routerState?.description || '',
    address: routerState?.address || '',
    unassignedLocation: routerState?.unassignedLocation || false,
    latitude: routerState?.coords ? routerState?.coords[0] : undefined,
    longitude: routerState?.coords ? routerState?.coords[1] : undefined,
    addressId: routerState?.addressId ? routerState?.addressId : undefined,
    countryUniqueId: routerState?.countryUniqueId || undefined,
    city: routerState?.city || undefined,
    marking: routerState?.marking || undefined,
    additionalData: routerState?.additionalData || {},
  };

  const schema = yup.object().shape({
    typeUniqueId: yup.string().required(t('general.field.validation.required')),
    transactionDate: yup
      .string()
      .required(t('general.field.validation.required'))
      .test(
        'date-validation',
        t('legalEntity.validation.maxDate').replace('{0}', '255'),
        (value) => {
          if (typeof value !== 'string') {
            return false;
          }
          const maxValidDate = new Date();
          const passedDate = new Date(value);
          return passedDate.valueOf() <= maxValidDate.valueOf();
        },
      ),
    attachments: yup.array().of(
      yup.object({
        attachmentName: yup
          .string()
          .test('unique', t('general.field.validation.unique'), (value, params) => {
            // @ts-expect-error: required by update
            const attachments = params?.from[1]?.value.attachments ?? [];
            if (!value) return true;
            if (attachments.filter((r: any) => r.attachmentName === value).length > 1) {
              return params.createError({
                path: params.path,
                message: t('general.field.validation.unique'),
              });
            }
            return true;
          })
          .required(t('general.field.validation.required'))
          .max(64, t('general.field.validation.maxCharacters').replace('{0}', '64'))
          .matches(/^[a-zA-Z0-9_ -]+$/, t('general.field.validation.attachmentName'))
          .trim(),
      }),
    ),
    legalEntityFullName: yup.string().when('typeUniqueId', {
      is: (typeVar: any) => CHANGE_LE_REQUIRED_TRANSACTION_TYPES.includes(typeVar),
      then: (yupSchema) => yupSchema.required(t('general.field.validation.required')),
    }),
    countryOfImportUniqueId: yup.string().when('typeUniqueId', {
      is: ETransactionTypes.DECLARED_IMPORTED,
      then: (yupSchema) => yupSchema.required(t('general.field.validation.required')),
    }),
    countryOfExportUniqueId: yup.string().when('typeUniqueId', {
      is: ETransactionTypes.EXPORTED,
      then: (yupSchema) => yupSchema.required(t('general.field.validation.required')),
    }),
    importer: yup
      .string()
      .max(250, t('general.field.validation.maxCharacters').replace('{0}', '250')),
    description: yup
      .string()
      .test(
        'editor-validation',
        t('general.field.validation.maxCharacters').replace('{0}', '1000'),
        (value) => countEditorCharacters(value) <= 1000,
      )
      .when('typeUniqueId', {
        is: ETransactionTypes.MODIFIED,
        then: (yupSchema) => yupSchema.required(t('general.field.validation.required')),
      }),
    expirationTime: yup.string().when('typeUniqueId', {
      is: (typeVar: any) =>
        [ETransactionTypes.ASSIGNED_TEMPORARILY, ETransactionTypes.LEASED].includes(typeVar),
      then: (yupSchema) =>
        yupSchema
          .required(t('general.field.validation.required'))
          .test(
            'expiration-before-transaction-date-validation',
            t('legalEntity.validation.expirationDateValidation').replace('{0}', '255'),
            (value, context) => {
              if (typeof value !== 'string') {
                return false;
              }
              return (
                new Date(value).valueOf() >= new Date(context.parent.transactionDate).valueOf()
              );
            },
          ),
    }),
    expirationDate: yup.string().when('typeUniqueId', {
      is: (typeVar: any) => [ETransactionTypes.TEST_FIRED].includes(typeVar),
      then: (yupSchema) =>
        yupSchema.test(
          'expiration-before-transaction-date-validation',
          t('legalEntity.validation.expirationDateValidation').replace('{0}', '255'),
          (value, context) => {
            if (typeof value !== 'string') {
              return false;
            }
            return (
              !value ||
              new Date(value).valueOf() >= new Date(context.parent.transactionDate).valueOf()
            );
          },
        ),
    }),
    marking: yup
      .object()
      .shape({
        marking: yup
          .string()
          .max(100, t('general.field.validation.maxCharacters').replace('{0}', '100'))
          .test('required', t('general.field.validation.required'), (value, params) => {
            // @ts-expect-error: required by update
            const typeId = params?.from[1]?.value.typeUniqueId;
            if (
              typeId === ETransactionTypes.SURPLUS_SALE ||
              typeId === ETransactionTypes.MARKED
            ) {
              return !!value;
            }
            return true;
          }),
      })
      .nullable(),
    additionalData: yup
      .object({
        yearOfConfiscation: yup
          .string()
          .transform((value, originalValue) =>
            originalValue === '' || originalValue === null ? undefined : value,
          )
          .max(100, t('general.field.validation.maxCharacters').replace('{0}', '100'))
          .nullable()
          .notRequired(),

        yearOfTransfer: yup
          .string()
          .test('required', t('general.field.validation.required'), (value, params) => {
            // @ts-expect-error: required by update
            const typeId = params?.from[1]?.value.typeUniqueId;
            if (typeId === ETransactionTypes.SURPLUS_SALE) {
              return !!value;
            }
            return true;
          })
          .transform((value, originalValue) =>
            originalValue === '' || originalValue === null ? undefined : value,
          )
          .max(100, t('general.field.validation.maxCharacters').replace('{0}', '100')),
        serialNumber: yup
          .string()
          .test('required', t('general.field.validation.required'), (value, params) => {
            // @ts-expect-error: required by update
            const typeId = params?.from[1]?.value.typeUniqueId;
            const isAmmunition =
              accessoryDetail?.ancillaryType?.toLowerCase() ===
              EArtAncillaryTypeNameLowerCase.AMMUNITION;
            if (
              typeId === ETransactionTypes.REPURPOSED &&
              !isAmmunition &&
              !accessoryDetail?.serialNumber
            ) {
              return !!value;
            }
            return true;
          }),
        lotNumber: yup
          .string()
          .test('required', t('general.field.validation.required'), (value, params) => {
            // @ts-expect-error: required by update
            const typeId = params?.from[1]?.value.typeUniqueId;
            const isAmmunition =
              accessoryDetail?.ancillaryType?.toLowerCase() ===
              EArtAncillaryTypeNameLowerCase.AMMUNITION;
            if (
              typeId === ETransactionTypes.REPURPOSED &&
              isAmmunition &&
              !accessoryDetail?.lotNumber
            ) {
              return !!value;
            }
            return true;
          }),
        batchNumber: yup
          .string()
          .test('required', t('general.field.validation.required'), (value, params) => {
            // @ts-expect-error: required by update
            const typeId = params?.from[1]?.value.typeUniqueId;
            const isAmmunition =
              accessoryDetail?.ancillaryType?.toLowerCase() ===
              EArtAncillaryTypeNameLowerCase.AMMUNITION;
            if (
              typeId === ETransactionTypes.REPURPOSED &&
              isAmmunition &&
              !accessoryDetail?.batchNumber
            ) {
              return !!value;
            }
            return true;
          }),
      })
      .nullable()
      .notRequired(),
    address: yup.string().nullable(),
    unassignedLocation: yup.boolean().nullable(),
    latitude: yup.number().nullable(),
    longitude: yup.number().nullable(),
    countryUniqueId: yup.string().nullable(),
    city: yup.string().nullable(),
  });

  const {
    control,
    watch,
    formState: { errors },
    handleSubmit,
    setError,
    setValue,
    getValues,
    clearErrors,
  } = useForm({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver,
  });

  const attachments = useFieldArray({
    control,
    name: 'attachments',
  });

  const transactionTypeValue = watch('typeUniqueId');
  const unassignedLocation = watch('unassignedLocation');
  const latitude = watch('latitude');
  const longitude = watch('longitude');
  const address = watch('address');
  const addressId = watch('addressId');

  useEffect(() => {
    if (unassignedLocation) {
      setValue('addressId', null);
    }
  }, [unassignedLocation]);

  const canChangeLegalEntity = [...CHANGE_LE_REQUIRED_TRANSACTION_TYPES].includes(
    transactionTypeValue,
  );

  const newOwnerIsKeeper = [ETransactionTypes.CONFISCATED].includes(transactionTypeValue);

  const getMarkingLabel = (transactionType = transactionTypeValue) => {
    switch (transactionType) {
      case ETransactionTypes.EXPORTED:
        return t('addTransaction.document.section.exportMarking.label');
      case ETransactionTypes.DEACTIVATED:
        return t('addTransaction.document.section.deactivationMarking.label');
      case ETransactionTypes.MARKED:
        return t('addTransaction.document.section.markedMarking.label');
      case ETransactionTypes.DECLARED_IMPORTED:
        return t('addFirearm.firearmList.section.importMarking.label');
      case ETransactionTypes.DECLARED_MANUFACTURED:
      case ETransactionTypes.DECLARED_UNKNOWN_ORIGIN:
      case ETransactionTypes.SEIZED:
      case ETransactionTypes.RECOVERED:
      case ETransactionTypes.SURRENDERED:
        return t('addFirearm.firearmList.section.appliedMarking.label');
      case ETransactionTypes.CONFISCATED:
        return t('addTransaction.document.section.confiscationMarking.label');
      case ETransactionTypes.SURPLUS_SALE:
        return t('addTransaction.document.section.surplusSaleMarking.label');
      default:
        return t('addTransaction.document.section.marking.label');
    }
  };

  const withoutLocationSelect = [
    ETransactionTypes.MODIFIED,
    ETransactionTypes.REPAIRED_OR_MAINTAINED,
    ETransactionTypes.EXPORTED,
    ETransactionTypes.PROOFED,
    ETransactionTypes.MARKED,
    ETransactionTypes.DEACTIVATED,
    ETransactionTypes.DESTROYED,
    ETransactionTypes.DEPLETED,
    ETransactionTypes.TEST_FIRED,
  ].includes(transactionTypeValue);

  const isShowMarking = [
    ETransactionTypes.DECLARED_IMPORTED,
    ETransactionTypes.EXPORTED,
    ETransactionTypes.DEACTIVATED,
    ETransactionTypes.MARKED,
    ETransactionTypes.DECLARED_MANUFACTURED,
    ETransactionTypes.DECLARED_UNKNOWN_ORIGIN,
    ETransactionTypes.RECOVERED,
    ETransactionTypes.CONFISCATED,
    ETransactionTypes.SURPLUS_SALE,
  ].includes(transactionTypeValue);

  const isReqiredMarking = [ETransactionTypes.SURPLUS_SALE].includes(transactionTypeValue);

  const isShowCountryOfImport = [ETransactionTypes.DECLARED_IMPORTED].includes(
    transactionTypeValue,
  );

  const isModificationDescription = [ETransactionTypes.MODIFIED].includes(
    transactionTypeValue,
  );

  const isShowCountryOfExport = transactionTypeValue === ETransactionTypes.EXPORTED;

  const canSetNewKeeperOnly = [
    ETransactionTypes.GIVEN,
    ETransactionTypes.LEASED,
    ETransactionTypes.ASSIGNED_TEMPORARILY,
    ETransactionTypes.ASSIGNED_INDEFINITELY,
    ETransactionTypes.RETURNED,
    ETransactionTypes.SEIZED,
  ].includes(transactionTypeValue);

  const isShowExpirationDate = [ETransactionTypes.TEST_FIRED].includes(transactionTypeValue);

  const isShowExpirationTime = [
    ETransactionTypes.ASSIGNED_TEMPORARILY,
    ETransactionTypes.LEASED,
  ].includes(transactionTypeValue);

  const isMapOnlyForOrganization = [
    ETransactionTypes.RECOVERED,
    ETransactionTypes.SEIZED,
    ETransactionTypes.SURRENDERED,
    ETransactionTypes.TRANSFERRED,
  ].includes(transactionTypeValue);

  const isShowYearOfTransfer = transactionTypeValue === ETransactionTypes.SURPLUS_SALE;
  const isShowYearOfConfiscation = transactionTypeValue === ETransactionTypes.CONFISCATED;
  const isShowSerialNumber =
    transactionTypeValue === ETransactionTypes.REPURPOSED &&
    accessoryDetail?.ancillaryType?.toLowerCase() !==
      EArtAncillaryTypeNameLowerCase.AMMUNITION;
  const isShowLotBatchNumber =
    transactionTypeValue === ETransactionTypes.REPURPOSED &&
    accessoryDetail?.ancillaryType?.toLowerCase() ===
      EArtAncillaryTypeNameLowerCase.AMMUNITION;

  const selectedNewLegalIfRequired =
    !canChangeLegalEntity || (canChangeLegalEntity && !!newAncillaryLocationOwner);

  useEffect(() => {
    (async () => {
      await loadDictionaries([EDictionaryTypes.CountryAll]);
    })();
  }, [selectedLanguage]);

  useEffect(() => {
    if (id) {
      getAncillaryLastTransactionUniqueId(id).then((result) =>
        setLastTransactionUniqueId(result.transactionUniqueId),
      );
    }
  }, [id]);

  useEffect(() => {
    (async () => {
      if (id) {
        await getAccessoryDetail(id);

        const transactionTypes = await getPossibleAncillariesTransactionType(id);
        if (transactionTypes) {
          setPossibleTransactionDictionaries(transactionTypes);
        }
      }
    })();
  }, [id, selectedLanguage]);

  useEffect(() => {
    if (transactionTypeValue && !canChangeLegalEntity) {
      setNewAncillaryLocationOwner(
        accessoryDetail?.keeperId || accessoryDetail?.ownerId || null,
      );
      setNewAncillaryLocationOwnerInserted(true);
    } else if (
      transactionTypeValue &&
      canChangeLegalEntity &&
      !newAncillaryLocationOwnerInserted &&
      routerState
    ) {
      setNewAncillaryLocationOwnerInserted(true);
      setNewAncillaryLocationOwner(routerState.legalEntityId);
    }
  }, [transactionTypeValue, accessoryDetail]);

  useEffect(() => {
    if (
      possibleTransactionDictionaries &&
      !possibleTransactionDictionaries?.map((d) => d.uniqueId).includes(transactionTypeValue)
    ) {
      setValue('typeUniqueId', '');
    }
  }, [possibleTransactionDictionaries]);

  const getReturnUrl = () => {
    if (accessoryDetail) {
      switch (accessoryDetail?.ancillaryType?.toLowerCase()) {
        case EArtAncillaryTypeNameLowerCase.ACCESSORY:
          return ERouteLinks.ViewAccessories.replace(':id', id);

        case EArtAncillaryTypeNameLowerCase.AMMUNITION:
          return ERouteLinks.ViewAmmunition.replace(':id', id);

        case EArtAncillaryTypeNameLowerCase.COMPONENTS:
          return ERouteLinks.ViewComponents.replace(':id', id);

        default:
          return '';
      }
    }
    return '';
  };

  const onSubmit = () => {
    let ownerId: any = null;
    let keeperId: any = null;
    setCreateTransactionError('');
    handleSubmit(async (data) => {
      setIsErrorLocation(false);
      const date = formatDateTimeToIso(data.transactionDate);

      if (canSetNewKeeperOnly) {
        keeperId = data?.legalEntityId;
        ownerId = accessoryDetail?.ownerId;
      } else if (
        [ETransactionTypes.STOLEN, ETransactionTypes.LOST].includes(transactionTypeValue)
      ) {
        keeperId = null;
        ownerId = accessoryDetail?.ownerId;
      } else if ([ETransactionTypes.CONFISCATED].includes(transactionTypeValue)) {
        keeperId = accessoryDetail?.keeperId;
        ownerId = accessoryDetail?.keeperId;
      } else if (
        [
          ETransactionTypes.EXPORTED,
          ETransactionTypes.DESTROYED,
          ETransactionTypes.DEPLETED,
        ].includes(transactionTypeValue)
      ) {
        keeperId = null;
        ownerId = null;
      } else if (routerState?.legalEntityId) {
        keeperId = routerState?.legalEntityId;
        ownerId = routerState?.legalEntityId;
      } else {
        keeperId = accessoryDetail?.keeperId;
        ownerId = accessoryDetail?.ownerId;
      }

      if (
        !withoutLocationSelect &&
        !(data.latitude && data.longitude) &&
        !data.unassignedLocation &&
        !addressId
      ) {
        setSubmitBlocked(false);
        return setIsErrorLocation(true);
      }

      const body = {
        request: {
          typeUniqueId: data.typeUniqueId,
          ...(keeperId ? { keeperId } : {}),
          ...(ownerId ? { ownerId } : {}),
          date,
          ancillaryUniqueId: id,
          ...(isShowMarking && isMiddleTransactionPage && data.marking.marking
            ? {
                marking: {
                  ancillaryUniqueId: id,
                  marking: data.marking.marking,
                  markingType: getMarkingType(data.typeUniqueId, data.marking.markingType),
                },
              }
            : {}),
          ...(isMiddleTransactionPage &&
          (isShowYearOfTransfer ||
            isShowYearOfConfiscation ||
            (isShowSerialNumber && !accessoryDetail?.serialNumber) ||
            (isShowLotBatchNumber &&
              (!accessoryDetail?.lotNumber || !accessoryDetail?.batchNumber)))
            ? {
                additionalData: {
                  ...(isShowYearOfTransfer && {
                    yearOfTransfer: data.additionalData.yearOfTransfer,
                  }),
                  ...(isShowYearOfConfiscation && {
                    yearOfConfiscation: data.additionalData.yearOfConfiscation,
                  }),
                  ...(isShowSerialNumber &&
                    !accessoryDetail?.serialNumber && {
                      serialNumber: data.additionalData.serialNumber,
                    }),
                  ...(isShowLotBatchNumber &&
                    !accessoryDetail?.lotNumber && {
                      lotNumber: data.additionalData.lotNumber,
                    }),
                  ...(isShowLotBatchNumber &&
                    !accessoryDetail?.batchNumber && {
                      batchNumber: data.additionalData.batchNumber,
                    }),
                },
              }
            : {}),
          ...(isShowCountryOfImport
            ? {
                countryOfImportUniqueId: data.countryOfImportUniqueId,
                importer: data.importer,
              }
            : {}),
          ...(isShowCountryOfExport
            ? {
                countryOfExportUniqueId: data.countryOfExportUniqueId,
              }
            : {}),
          ...(!withoutLocationSelect && (routerState?.coords || data.addressId)
            ? {
                latitude: data.addressId ? data.latitude : routerState?.coords[0],
                longitude: data.addressId ? data.longitude : routerState?.coords[1],
                address: data.addressId ? data.address : routerState?.address,
                city: data.addressId ? data.city : routerState?.city,
                countryUniqueId: data.addressId
                  ? data.countryUniqueId
                  : routerState?.countryUniqueId,
                unassignedLocation: data.unassignedLocation,
              }
            : {}),
          ...(!withoutLocationSelect && !routerState?.coords
            ? {
                unassignedLocation: data.unassignedLocation,
              }
            : {}),
          ...(isModificationDescription ? { description: data.description } : {}),
          ...(isShowExpirationDate
            ? {
                expirationDate: data.expirationDate
                  ? formatDateTimeToIso(data.expirationDate)
                  : null,
              }
            : {}),
          ...(isShowExpirationTime
            ? {
                expirationDate: formatDateTimeToIso(data.expirationTime),
              }
            : {}),
          lastTransactionUniqueId,
        },
        ...(data.attachments?.filter((attachment: any) => attachment.file !== null)?.length
          ? { attachments: data.attachments }
          : {}),
      };

      try {
        setSubmitBlocked(true);
        const transaction = await createAncillaryMiddleTransaction(body);
        if (transaction) {
          setAlert(ActionTypes.SET_TRANSACTION_ALERT, {
            text: t('addTransaction.transactionAdded.text'),
          });
          navigate(pathname, {
            state: {
              ...routerState,
              typeUniqueId: undefined,
              transactionDate: undefined,
              countryOfImportUniqueId: undefined,
              importer: undefined,
              countryOfExportUniqueId: undefined,
              expirationDate: undefined,
              expirationTime: undefined,
              attachments: undefined,
              legalEntityFullName: undefined,
              legalEntityId: undefined,
              address: undefined,
              currentKeeperUniqueId: undefined,
              currentOwnerUniqueId: undefined,
              type: undefined,
              breadcrumbs: undefined,
            },
            replace: true,
          });
          navigate(getReturnUrl());
        }
      } catch (e: any) {
        setSubmitBlocked(false);
        setCreateTransactionError(e?.response?.data?.message);
      }

      return true;
    })();
  };

  useEffect(() => {
    (async function () {
      scrollToError(errors);
    })();
  }, [errors]);

  const validateFile = (file: File, index: number) => {
    const fileType: any = file.type;
    if (file) {
      if (file.size === 0) {
        setError(`attachments[${index}].file`, {
          type: 'custom',
          message: t('addAttachment.emptyFile.text'),
        });
        return false;
      }
      const fileSizeInMB = file.size / 1024 / 1024;
      if (fileSizeInMB > MAX_FILE_SIZE_IN_MB) {
        setError(`attachments[${index}].file`, {
          type: 'custom',
          message: t('addAttachment.fileSizeValidation.text'),
        });
        return false;
      }
      if (
        ![
          EMIMETypes.JPG,
          EMIMETypes.JPEG,
          EMIMETypes.PNG,
          EMIMETypes.PDF,
          EMIMETypes.XLSX,
        ].includes(fileType)
      ) {
        setError(`attachments[${index}].file`, {
          type: 'custom',
          message: t('addAttachment.fileTypeValidation.text'),
        });
        return false;
      }
    }
    clearErrors('attachments');
    return true;
  };

  const handleUploadFile = (file: File, index: number) => {
    if (validateFile(file, index)) {
      attachments.update(index, {
        file,
      });
    } else {
      attachments.update(index, {
        file: null,
        attachmentName: '',
      });
    }
  };

  const clearUserInput = () => {
    setValue('description', '');
    setValue('countryOfImportUniqueId', '');
    setValue('countryOfExportUniqueId', '');
    setValue('expirationDate', '');
    setValue('expirationTime', '');
    setValue('importer', '');
    setValue('legalEntityId', '');
    setValue('legalEntityFullName', '');
    setValue('address', '');
    setValue('countryUniqueId', null);
    setValue('city', null);
    setValue('latitude', null);
    setValue('addressId', null);
    setValue('longitude', null);
    setValue('unassignedLocation', false);

    setNewAncillaryLocationOwner(null);
  };

  const renderRemoveAttachmentButton = (index: number) => (
    <Grid
      size={{ xs: 1 }}
      sx={!isDesktop ? { display: 'flex', justifyContent: 'flex-end' } : {}}
    >
      <Button
        variant={EButtonVariants.outlined}
        icon={EIconTypes.delete}
        onClick={() => attachments.remove(index)}
        sizePX={EButtonSizesPX.px32}
        sx={{ ...(isDesktop ? { marginLeft: '20px' } : {}), marginTop: '28px' }}
      />
    </Grid>
  );

  const handleLimitToParam = () => {
    if (isMapOnlyForOrganization) {
      return `?limitTo=${LETypes.organization}`;
    }
    if (transactionTypeValue === ETransactionTypes.SURPLUS_SALE) {
      return `?prefixGroup=${EDictionaryPrefixTypesUniqueId.civilianPrefix}&organisationCategory=${EOrganisationCategory.civilian}`;
    }
    return '';
  };

  const filterTransactionTypeOptions = (
    transactions: IDictionary[] | null,
  ): IDictionary[] | null => {
    if (transactions === null) {
      return null;
    }
    if (accessoryDetail?.keeperType === LETypes.organization) {
      DISABLED_FOR_TRANSACTION_FORM = [ETransactionTypes.GIVEN];
    }
    if (accessoryDetail?.keeperType === LETypes.individual) {
      DISABLED_FOR_TRANSACTION_FORM = [
        ETransactionTypes.ASSIGNED_INDEFINITELY,
        ETransactionTypes.ASSIGNED_TEMPORARILY,
        ETransactionTypes.LEASED,
        ETransactionTypes.TRANSFERRED,
        ETransactionTypes.DESTROYED,
        ETransactionTypes.MARKED,
        ETransactionTypes.DEACTIVATED,
        ETransactionTypes.PROOFED,
      ];
    }
    return transactions.filter(
      (transaction) =>
        !DISABLED_FOR_TRANSACTION_FORM.map((disabled) => disabled.toString()).includes(
          transaction.uniqueId.toString(),
        ),
    );
  };

  const breadcrumbs = [
    { label: t('module.ancillaries.name'), route: ERouteLinks.Ancillaries },
    {
      label: t('ancillaries.ancillaryList.label'),
      route: ERouteLinks.ViewAncillary.replace(':id', id).replace(
        ':type',
        accessoryDetail?.ancillaryType?.toLowerCase() as string,
      ),
    },
    t('addTransaction.addTransaction.breadcrumb'),
  ];

  const attachmentNames = watch('attachments');

  useEffect(() => {
    attachments.fields.forEach((attachment: any, index) => {
      if (attachment?.file?.name && !attachmentNames[index]?.attachmentName) {
        setValue(
          `attachments.${index}.attachmentName`,
          removeFileExtension(attachment.file.name),
        );
      }
    });
  }, [attachments.fields, attachmentNames]);

  const canSelectFromMap = typesWithoutStrictAddressLocation.includes(
    transactionTypeValue as ETransactionTypes,
  );

  return (
    <>
      <Breadcrumbs items={breadcrumbs} />
      <Box sx={{ mb: 2 }}>
        <StyledPageTitle variant="h4">
          {t('addTransaction.addTransaction.header')}
        </StyledPageTitle>
      </Box>
      <div ref={elRef}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 3 }}>
            <FormSearchSelect
              options={mapDictionaryToOptionProp(
                filterTransactionTypeOptions(possibleTransactionDictionaries),
              )}
              name="typeUniqueId"
              label={t('addFirearm.location.section.transactionType')}
              control={control}
              onSelect={clearUserInput}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <FormDateTimePicker
              name="transactionDate"
              label={t('addFirearm.location.section.transactionDate')}
              control={control}
              errors={errors}
              currentHour={true}
            />
          </Grid>
        </Grid>

        <Grid container>
          {accessoryDetail && (
            <Grid size={{ xs: 12 }}>
              <Accordion
                title={t('addTransaction.currentOwnerAndKeeper.section.name')}
                alwaysExpanded
              >
                <Box sx={{ mb: 5 }}>
                  <div>
                    <StyledDetailsWrapper container spacing={3}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t('addTransaction.currentOwnerAndKeeper.section.owner.label')}
                        </Typography>
                        <Typography variant="body2">
                          <StyledLegalLink
                            legalId={accessoryDetail.ownerId}
                            legalType={mapILegalEntityTypeToELegalEntityTypes(
                              accessoryDetail.ownerType,
                            )}
                            labelText={accessoryDetail.ownerName}
                            legalVisibilityUniqueId={accessoryDetail.ownerVisibilityUniqueId}
                          />
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                          {t('addTransaction.currentOwnerAndKeeper.section.keeper.label')}
                        </Typography>
                        <Typography variant="body2">
                          <StyledLegalLink
                            legalId={accessoryDetail.keeperId}
                            legalType={mapILegalEntityTypeToELegalEntityTypes(
                              accessoryDetail.keeperType,
                            )}
                            labelText={accessoryDetail.keeperName}
                            legalVisibilityUniqueId={accessoryDetail.keeperVisibilityUniqueId}
                          />
                        </Typography>
                      </Grid>
                    </StyledDetailsWrapper>
                  </div>
                </Box>
              </Accordion>
            </Grid>
          )}

          {newOwnerIsKeeper && accessoryDetail && (
            <Grid size={{ xs: 12 }}>
              <Accordion
                title={t('addTransaction.newOwnerAndKeeper.section.name')}
                alwaysExpanded
              >
                <Box sx={{ mb: 5 }}>
                  <div>
                    <StyledDetailsWrapper container spacing={3}>
                      {[
                        'addTransaction.currentOwnerAndKeeper.section.owner.label',
                        'addTransaction.currentOwnerAndKeeper.section.keeper.label',
                      ].map((title) => (
                        <Grid key={title} size={{ xs: 12, md: 6 }}>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(title)}
                          </Typography>
                          <Typography variant="body2">
                            <StyledLegalLink
                              legalId={accessoryDetail.keeperId}
                              legalType={mapILegalEntityTypeToELegalEntityTypes(
                                accessoryDetail.keeperType,
                              )}
                              labelText={accessoryDetail.keeperName}
                              legalVisibilityUniqueId={
                                accessoryDetail.keeperVisibilityUniqueId
                              }
                            />
                          </Typography>
                        </Grid>
                      ))}
                    </StyledDetailsWrapper>
                  </div>
                </Box>
              </Accordion>
            </Grid>
          )}

          {canChangeLegalEntity && (
            <Grid size={{ xs: 12 }}>
              <Accordion
                title={
                  [
                    ETransactionTypes.ASSIGNED_INDEFINITELY,
                    ETransactionTypes.ASSIGNED_TEMPORARILY,
                    ETransactionTypes.GIVEN,
                    ETransactionTypes.RETURNED,
                    ETransactionTypes.LEASED,
                    ETransactionTypes.SEIZED,
                  ].includes(transactionTypeValue)
                    ? t('addTransaction.newKeeper.section.name')
                    : t('addTransaction.newOwnerAndKeeper.section.name')
                }
                alwaysExpanded
              >
                <Grid container size={{ xs: 12 }} rowSpacing={2} sx={{ mb: 5 }}>
                  <Grid size={{ xs: 12, lg: 6 }}>
                    <FormInput
                      name="legalEntityFullName"
                      label={`${t(
                        'addTransaction.newOwnerAndKeeper.section.legalEntity.label',
                      )}`}
                      control={control}
                      errors={errors}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid
                    size={{ xs: 12, lg: 3 }}
                    sx={{ marginTop: { lg: '28px' }, ml: { lg: 2 } }}
                  >
                    <Button
                      id="addTransaction.newOwnerAndKeeper.section.searchForLegalEntity.button"
                      fullWidth
                      label={t(
                        'addTransaction.newOwnerAndKeeper.section.searchForLegalEntity.button',
                      )}
                      variant={EButtonVariants.contained}
                      size={EButtonSizes.small}
                      onClick={() =>
                        navigate(`${ERouteLinks.SelectLegalEntity}${handleLimitToParam()}`, {
                          state: {
                            context: 'ancillaryTransactions',
                            ...routerState,
                            ...getValues(),
                            address: null,
                            latitude: null,
                            longitude: null,
                            addressId: null,
                            countryUniqueId: null,
                            unassignedLocation: false,
                            id,
                            currentKeeperUniqueId: accessoryDetail?.keeperId,
                            currentOwnerUniqueId:
                              transactionTypeValue === ETransactionTypes.SOLD
                                ? accessoryDetail?.ownerId
                                : undefined,
                            breadcrumbs: [
                              ...breadcrumbs,
                              t('ancillaries.transaction.selectLegal.label'),
                            ],
                            returnLink: `${ERouteLinks.AncillariesMiddleTransaction.replace(
                              ':id',
                              id,
                            )}`,
                          },
                        })
                      }
                    />
                  </Grid>
                </Grid>
              </Accordion>
            </Grid>
          )}

          <Grid size={{ xs: 12 }}>
            <Accordion
              title={t('addTransaction.ancillaryDetails.section.name')}
              alwaysExpanded
            />
            <Grid
              container
              sx={{
                border: `1px solid ${colors.primary25}`,
                borderRadius: '4px',
                overflow: 'hidden',
                mb: 4,
              }}
            >
              <Grid
                size={{ xs: 12 }}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  backgroundColor: colors.primary100,
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
                  gap: 1,
                  px: 2,
                  py: 1,
                }}
              >
                <Grid sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {accessoryDetail?.type}
                  </Typography>
                </Grid>
                <Grid sx={{ width: isMobile ? '100%' : 'auto', flexShrink: 0 }}>
                  <Link
                    href={ERouteLinks.ViewAncillary.replace(
                      ':type',
                      accessoryDetail?.ancillaryType?.toLowerCase() as string,
                    ).replace(':id', accessoryDetail?.uniqueId as string)}
                    underline="none"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: 'block', width: '100%' }}
                  >
                    <Button
                      label={t('general.details.text')}
                      variant={EButtonVariants.outlined}
                      size={EButtonSizes.small}
                      id="addTransaction_firearmDetails_details-button"
                      fullWidth={isMobile}
                    />
                  </Link>
                </Grid>
              </Grid>

              <Grid container size={{ xs: 12 }} rowSpacing={2} sx={{ p: 2 }}>
                <Grid size={{ xs: 12, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('ancillary.transaction.details.ancillary.label')}
                    </Typography>
                    <Typography variant="body2">
                      {accessoryDetail?.ancillaryType
                        ? capitalizeFirstLetter(accessoryDetail.ancillaryType.toLowerCase())
                        : '-'}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('ancillary.transaction.details.type.label')}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(accessoryDetail?.type)}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('ancillary.transaction.details.artId.label')}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(accessoryDetail?.artId)}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, lg: 3 }}>
                  <Stack>
                    <Link
                      href={`${ERouteLinks.ViewAncillaries.replace(
                        ':id',
                        accessoryDetail?.artUniqueId as string,
                      ).replace(
                        ':type',
                        accessoryDetail?.ancillaryType?.toLowerCase() as string,
                      )}`}
                      underline="none"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        fullWidth
                        label={t('ancillary.transaction.details.showArt.label')}
                        variant={EButtonVariants.contained}
                        size={EButtonSizes.small}
                      />
                    </Link>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('ancillary.transaction.details.manufacturer.label')}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(accessoryDetail?.manufacturer)}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('ancillary.transaction.details.calibre.label')}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(accessoryDetail?.calibre)}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('ancillary.transaction.details.composition.label')}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(accessoryDetail?.composition)}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('ancillary.transaction.details.proofHouse.label')}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(accessoryDetail?.proofHouse)}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid
                  size={{ xs: 12 }}
                  style={{
                    height: '1px',
                    backgroundColor: colors.primary100,
                    padding: 0,
                    margin: '20px 0 10px',
                  }}
                />

                <Grid size={{ xs: 12, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('ancillary.transaction.details.fireCycleId.label')}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(accessoryDetail?.fireCycleId)}
                    </Typography>
                  </Stack>
                </Grid>
                {!(
                  accessoryDetail?.ancillaryType ===
                  EArtAncillaryTypeName.AMMUNITION.toUpperCase()
                ) && (
                  <Grid size={{ xs: 12, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t('ancillary.transaction.details.serialNumber.label')}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(accessoryDetail?.serialNumber)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {accessoryDetail?.ancillaryType ===
                  EArtAncillaryTypeName.AMMUNITION.toUpperCase() && (
                  <Grid size={{ xs: 12, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t('addAncillary.list.section.lotNumber.label')}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(accessoryDetail?.lotNumber)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                {accessoryDetail?.ancillaryType ===
                  EArtAncillaryTypeName.AMMUNITION.toUpperCase() && (
                  <Grid size={{ xs: 12, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t('addAncillary.list.section.batchNumber.label')}
                      </Typography>
                      <Typography variant="body2">
                        {emptyFieldHolder(accessoryDetail?.batchNumber)}
                      </Typography>
                    </Stack>
                  </Grid>
                )}

                <Grid size={{ xs: 12, lg: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('addAncillary.list.section.appliedOrImportMarking.label')}
                    </Typography>
                    <Typography variant="body2">
                      {emptyFieldHolder(accessoryDetail?.appliedOrImportMarking)}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {!withoutLocationSelect && !!transactionTypeValue && selectedNewLegalIfRequired && (
            <Grid size={{ xs: 12 }}>
              <Accordion title={t('addTransaction.location.section.name')} alwaysExpanded>
                <Grid container columnSpacing={2} sx={{ mb: 1, pl: 1 }}>
                  <Grid size={{ xs: 12, lg: 4 }}>
                    <FormCheckbox
                      name=""
                      id="unassignedLocation"
                      checked={unassignedLocation}
                      label={t('addTransaction.location.section.unassignedLocation.label')}
                      control={control}
                      errors={errors}
                      disabled={canChangeLegalEntity && !newAncillaryLocationOwner}
                      onChange={(newState) => {
                        if (newState) setUnassignedLocationModalOpened(true);
                        else setValue('unassignedLocation', false);
                      }}
                    />
                  </Grid>
                </Grid>
                <SelectLocationTransactionPage
                  show={!unassignedLocation}
                  searchAddressesFor={newAncillaryLocationOwner}
                  canSelectOnlyExistingAddress={!canSelectFromMap}
                  addressId={addressId}
                  setValue={setValue}
                />
                {!unassignedLocation && (
                  <Grid
                    size={{ lg: 12 }}
                    sx={{
                      mb: 4,
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'flex-start',
                      columnGap: 3,
                    }}
                  >
                    {canSelectFromMap && (
                      <Grid sx={{ flexBasis: '40%' }}>
                        <FormInput
                          name="address"
                          label={t('addTransaction.location.section.address.text')}
                          control={control}
                          errors={errors}
                          placeholder={address}
                          InputProps={{ readOnly: true }}
                          disabled
                          sx={{ minWidth: '272px' }}
                          helperText={t('addTransaction.location.section.address.helperText')}
                        />
                      </Grid>
                    )}
                    {latitude && longitude && (
                      <StyledLocationMarkConfirmationMarginBottom>
                        <Typography variant="body1" sx={{ color: colors.success900 }}>
                          {t('addTransaction.location.section.locationMarked.text')}
                        </Typography>
                        <StyledIconWrapper>
                          <Icon
                            type={EIconTypes.checkmark}
                            color={colors.white}
                            fontSize={14}
                          />
                        </StyledIconWrapper>
                      </StyledLocationMarkConfirmationMarginBottom>
                    )}
                    {canSelectFromMap && (
                      <Button
                        id="addTransaction_location_select-button"
                        onClick={() =>
                          navigate(`${ERouteLinks.SelectLocation.replace(':id', id)}`, {
                            state: {
                              ...routerState,
                              ...getValues(),
                              coords:
                                latitude && longitude ? [latitude, longitude] : undefined,
                              breadcrumbs: [
                                ...breadcrumbs,
                                t('selectLocationOnMap.page.text'),
                              ],
                              leId: accessoryDetail?.keeperId || accessoryDetail?.ownerId,
                              searchAddressesFor: newAncillaryLocationOwner,
                              canSelectOnlyExistingAddress: !canSelectFromMap,
                              returnLink: `${ERouteLinks.AncillariesMiddleTransaction.replace(
                                ':id',
                                id,
                              )}`,
                            },
                          })
                        }
                        fullWidth={!isDesktop}
                        label={t('selectLocationOnMap.page.text')}
                        variant={EButtonVariants.outlined}
                        size={EButtonSizes.small}
                        sx={{ marginTop: '28px' }}
                      />
                    )}
                    {isErrorLocation && (
                      <Grid size={{ xs: 12 }}>
                        <FormErrorLabel
                          label={
                            canSelectFromMap
                              ? t(
                                  'addTransaction.location.section.selectLocationOnMap.errorLabel',
                                )
                              : t('addTransaction.location.section.selectLocation.errorLabel')
                          }
                        />
                      </Grid>
                    )}
                  </Grid>
                )}
              </Accordion>
            </Grid>
          )}

          {isShowCountryOfImport && (
            <Grid size={{ xs: 12 }}>
              <Accordion title={t('addFirearm.countryOfImport.section.name')} alwaysExpanded>
                <Grid container columnSpacing={2} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 12, lg: 6 }}>
                    <FormInput
                      name="importer"
                      label={t('addFirearm.countryOfImport.section.importer.label.validation')}
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, lg: 3 }}>
                    <FormSelect
                      options={mapDictionaryToOptionProp(countryAll)}
                      name="countryOfImportUniqueId"
                      setValue={setValue}
                      label={t('addFirearm.countryOfImport.section.country.label.validation')}
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                </Grid>
              </Accordion>
            </Grid>
          )}

          {isShowCountryOfExport && (
            <Grid size={{ xs: 12 }}>
              <Accordion title={t('addFirearm.countryOfExport.section.name')} alwaysExpanded>
                <Box sx={{ mb: 4 }}>
                  <Grid size={{ xs: 12, lg: 6 }}>
                    <FormSelect
                      options={mapDictionaryToOptionProp(countryAll)}
                      name="countryOfExportUniqueId"
                      label={t('addFirearm.countryOfExport.section.country.label.validation')}
                      setValue={setValue}
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                </Box>
              </Accordion>
            </Grid>
          )}
          {isShowMarking && isMiddleTransactionPage && (
            <Grid size={{ xs: 12 }}>
              <Accordion title={t('addAncillary.marking.section.name')} alwaysExpanded>
                <Box sx={{ mb: 4 }}>
                  {transactionTypeValue === ETransactionTypes.MARKED ? (
                    <MarkingsFormForMarked
                      item={accessoryDetail}
                      isGroupTransaction={false}
                      setValue={setValue}
                      control={control}
                      errors={errors}
                      name={`marking.marking`}
                    />
                  ) : (
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <FormInput
                        name="marking.marking"
                        label={getMarkingLabel()}
                        setValue={setValue}
                        control={control}
                        errors={errors}
                        required={isReqiredMarking}
                      />
                    </Grid>
                  )}

                  {isShowYearOfConfiscation && (
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <FormInput
                        name="additionalData.yearOfConfiscation"
                        label={t('addTransaction.document.section.yearOfConfiscation.label')}
                        setValue={setValue}
                        control={control}
                        errors={errors}
                      />
                    </Grid>
                  )}
                  {isShowYearOfTransfer && (
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <FormInput
                        name="additionalData.yearOfTransfer"
                        label={t('addTransaction.document.section.yearOfTransfer.label')}
                        setValue={setValue}
                        control={control}
                        errors={errors}
                        required
                      />
                    </Grid>
                  )}
                </Box>
              </Accordion>
            </Grid>
          )}
          {isShowSerialNumber &&
            isMiddleTransactionPage &&
            !accessoryDetail?.serialNumber &&
            accessoryDetail?.ancillaryType?.toLowerCase() !=
              EArtAncillaryTypeNameLowerCase.AMMUNITION && (
              <Grid size={{ xs: 12 }}>
                <Accordion
                  title={t('addTransaction.document.section.serialNumber.label')}
                  alwaysExpanded
                >
                  <Box sx={{ mb: 4 }}>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <FormInput
                        name="additionalData.serialNumber"
                        label={t('addTransaction.document.section.serialNumber.label')}
                        setValue={setValue}
                        control={control}
                        errors={errors}
                        required
                      />
                    </Grid>
                  </Box>
                </Accordion>
              </Grid>
            )}
          {isShowLotBatchNumber &&
            isMiddleTransactionPage &&
            accessoryDetail?.ancillaryType?.toLowerCase() ===
              EArtAncillaryTypeNameLowerCase.AMMUNITION &&
            (!accessoryDetail?.lotNumber || !accessoryDetail?.batchNumber) && (
              <Grid size={{ xs: 12 }}>
                <Accordion
                  title={t('addTransaction.document.section.batchLotNumber.label')}
                  alwaysExpanded
                >
                  <Box sx={{ mb: 4 }}>
                    <Grid container spacing={4}>
                      {!accessoryDetail?.lotNumber && (
                        <Grid size={{ xs: 12, lg: 3 }}>
                          <FormInput
                            name="additionalData.lotNumber"
                            label={`${t('addAncillary.list.section.lotNumber.label')}`}
                            setValue={setValue}
                            control={control}
                            charsLimit={10}
                            errors={errors}
                          />
                        </Grid>
                      )}
                      {!accessoryDetail?.batchNumber && (
                        <Grid size={{ xs: 12, lg: 3 }}>
                          <FormInput
                            name="additionalData.batchNumber"
                            label={`${t('addAncillary.list.section.batchNumber.label')}`}
                            setValue={setValue}
                            control={control}
                            charsLimit={10}
                            errors={errors}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Accordion>
              </Grid>
            )}
          {isShowExpirationDate && (
            <Grid size={{ xs: 12 }}>
              <Accordion title={t('addTransaction.expiration.section.name')} alwaysExpanded>
                <Box sx={{ mb: 4 }}>
                  <Grid size={{ xs: 12, lg: 6 }}>
                    <FormDatePicker
                      name="expirationDate"
                      label={t(
                        'addTransaction.expiration.section.expirationDate.label.validation',
                      )}
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                </Box>
              </Accordion>
            </Grid>
          )}

          {isShowExpirationTime && (
            <Grid size={{ xs: 12 }}>
              <Accordion title={t('addTransaction.expiration.section.name')} alwaysExpanded>
                <Box sx={{ mb: 4 }}>
                  <Grid size={{ xs: 12, lg: 6 }}>
                    <FormDateTimePicker
                      name="expirationTime"
                      label={`${t(
                        'addTransaction.expiration.section.expirationTime.label.validation',
                      )}*`}
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                </Box>
              </Accordion>
            </Grid>
          )}

          {isModificationDescription && (
            <Grid size={{ xs: 12 }}>
              <Accordion
                title={t('ancillaries.transaction.modification.label')}
                alwaysExpanded
              >
                <Box sx={{ mb: 4 }}>
                  <Grid size={{ xs: 12 }}>
                    <FormWyswigEditor
                      name="description"
                      label={`${t('ancillaries.transaction.modification.label')}*`}
                      placeholder={t('ancillaries.transaction.modification.label')}
                      setValue={setValue}
                      control={control}
                      errors={errors}
                      setError={setError}
                      clearErrors={clearErrors}
                      charsLimit={1000}
                    />
                  </Grid>
                </Box>
              </Accordion>
            </Grid>
          )}

          <Grid size={{ xs: 12 }}>
            <Accordion title={t('addTransaction.document.section.name')} alwaysExpanded>
              <Box>
                <Grid container>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ mb: 2 }}>
                      <Alert
                        text={t('general.field.availableAttachmentFormats.text')}
                        variant={EAlertVariants.warning}
                      />
                    </Box>
                  </Grid>
                  {attachments?.fields?.map((attachment: any, index) => (
                    <Grid
                      key={attachment.id}
                      container
                      size={{ xs: 12 }}
                      sx={{ mb: !isDesktop ? 2 : 0 }}
                    >
                      <Grid size={{ xs: attachments.fields?.length > 1 ? 11 : 12, lg: 6 }}>
                        <FormInput
                          name={`attachments[${index}].attachmentName`}
                          label={t('addTransaction.document.section.attachmentName.label')}
                          defaultValue={
                            attachment?.file?.name
                              ? removeFileExtension(attachment?.file?.name)
                              : ''
                          }
                          control={control}
                          errors={errors}
                          arrayName="attachments"
                          arrayIndex={index}
                          fieldName="attachmentName"
                          InputProps={{ disabled: attachment?.file === null }}
                        />
                      </Grid>
                      {!isDesktop && renderRemoveAttachmentButton(index)}
                      <Grid
                        size={{ xs: 12, lg: 3 }}
                        sx={{ marginTop: { lg: '28px' }, ml: { lg: 2 } }}
                      >
                        <Button
                          isUploadButton
                          onClick={(files) => handleUploadFile(files[0], index)}
                          fullWidth
                          label={t('addTransaction.document.section.browseFiles.button')}
                          id="add_file-button"
                          icon={EIconTypes.plus}
                          size={EButtonSizes.small}
                          isMultiple
                        />
                        {errors &&
                          errors?.attachments &&
                          getAttachmentError(errors, index) && (
                            <FormErrorLabel
                              label={getAttachmentError(errors, index)?.message ?? ''}
                            />
                          )}
                      </Grid>
                      {isDesktop && renderRemoveAttachmentButton(index)}
                    </Grid>
                  ))}
                  <Grid size={{ xs: 12, lg: 3 }} sx={{ mt: { xs: 2, lg: 0 } }}>
                    <Button
                      onClick={() =>
                        attachments.append({
                          attachmentName: '',
                          file: null,
                        })
                      }
                      fullWidth
                      id="add_file-button"
                      label={t('addFirearm.document.section.addFile.button')}
                      variant={EButtonVariants.outlined}
                      size={EButtonSizes.small}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Accordion>
          </Grid>

          <Grid container size={{ xs: 12 }} sx={{ mt: 4, justifyContent: 'flex-start' }}>
            <Grid size={{ xs: 12, lg: 6 }} container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Button
                  onClick={onSubmit}
                  fullWidth
                  label={t('addTransaction.save.button')}
                  disabled={accessoryDetail === undefined}
                  variant={EButtonVariants.contained}
                  isLoading={submitBlocked}
                  sx={{ mr: 1 }}
                  id="addTransaction-save-button"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Button
                  fullWidth
                  id="addTransaction-cancel-button"
                  label={t('addTransaction.cancel.button')}
                  variant={EButtonVariants.outlined}
                  onClick={() => navigate(getReturnUrl())}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption">{t('general.mandatoryField.text')}</Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ mt: 4 }}>
            {createTransactionError && (
              <Box sx={{ mb: 2 }}>
                <Alert text={createTransactionError} variant={EAlertVariants.error} />
              </Box>
            )}
          </Grid>
        </Grid>
      </div>
      <FormModal
        open={unassignedLocationModalOpened}
        onClickClose={() => {
          setUnassignedLocationModalOpened(false);
          setValue('unassignedLocation', false);
        }}
        onClickApprove={() => {
          setValue('unassignedLocation', true);
          setUnassignedLocationModalOpened(false);
        }}
        title={t('transaction.unassigned.modal.title')}
        text={t('transaction.middle.unassigned.modal.text')}
        approveButtonLabel="general.confirm.label"
      />
    </>
  );
};

export default AncillariesAddTransactionPage;
